/**
 * [PM-SG 2026-08-12] PARC FERMÉ — the top three, rebuilt.
 *
 * WHAT THIS REPLACES. The old page was the Apps Script board's rostrum: three metal-tinted
 * columns carrying giant 1 / 2 / 3 numerals, the retired top-down car, and a canvas of a car
 * doing donuts whose smoke trail floated across the header with nothing under it. Two problems.
 * It was generic — swap the metals for any other palette and it is the podium graphic every
 * awards app ships. And it was the only page still drawing the OLD car, so the board had two
 * different cars depending on which page you were looking at.
 *
 * THE IDEA. The real image at the end of a Grand Prix is not a rostrum, it is PARC FERMÉ: the
 * top three cars pulled into numbered bays, the position painted on the tarmac under them,
 * nobody allowed to touch anything. That is far more specific to the sport than a podium
 * silhouette, and it lets the page use the board's OWN car — the same profile car that races on
 * the RACE page — instead of an ornament that appears nowhere else.
 *
 * So: three bays on asphalt, the position painted on the floor in perspective, each PM's car
 * parked in its own livery, a metal kerb across the front of each bay. The hierarchy that the
 * rostrum's steps used to carry is now the size of the bay, which reads from further away than
 * a step does.
 *
 * THE MOTION, because a still page on a wall is a photograph:
 *   ARRIVAL   the cars drive in from the right and brake into their bay, and they arrive in
 *             REVERSE ORDER — third, second, winner — which is both better drama and true to
 *             the sport, where the winner is last back because of the extra lap.
 *   IDLE      heat off the engine cover and the exhaust still settling, plus the travelling
 *             highlight on each metal kerb. All of it is life, none of it encodes a number.
 * Nothing moves under the figures themselves: those are read from across a room.
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  SideCarSvg, SIDE_CAR_RATIO, Exhaust, LANE_SURFACE, TEXT, RED, EASE, LINE,
  prefersReducedMotion,
} from './chrome';
import { Runner, LeaderData, fmtNum } from './raceModel';

/* Desaturated on purpose: a bright gold would read as the amber this board already uses for
   "not classified". */
const METALS = [
  { m: '#D9B36C', soft: 'rgba(217,179,108,.30)' }, // gold
  { m: '#C8CDD6', soft: 'rgba(200,205,214,.24)' }, // silver
  { m: '#B07A4E', soft: 'rgba(176,122,78,.26)' },  // bronze
];

/* ═════════════════════════════════════════════════════════════ motion ═════ */

/**
 * In from the LEFT, braking into the bay — the nose dips as it stops, then settles.
 *
 * [2026-08-12] It used to arrive from the right, which was backwards in two ways: the car faces
 * right, so it was reversing into its bay nose-first-backwards, and every other car on this board
 * — the RACE field, the PIT WALL delivery — travels left to right. A page where the cars run the
 * other way reads as a different product.
 */
const rollIn = keyframes`
  0%   { transform: translateX(-190%) rotate(0deg); opacity: 0; }
  8%   { opacity: 1; }
  72%  { transform: translateX(-9%) rotate(-1.1deg); }
  88%  { transform: translateX(1.5%) rotate(0.5deg); }
  100% { transform: translateX(0) rotate(0deg); }
`;
const bayIn = keyframes`from { opacity: 0; transform: translateY(32.4px); }`;
const paintIn = keyframes`from { opacity: 0; }`;
/** Heat off a hot engine cover: slow, uneven, and never over a figure. */
const shimmer = keyframes`
  0%, 100% { transform: translateY(0) scaleX(1); opacity: .34; }
  50%      { transform: translateY(-14%) scaleX(1.06); opacity: .16; }
`;
/** The one continuous cue on each kerb, same idea as the kerbs elsewhere on the board. */
const kerbRun = keyframes`
  from { transform: translateX(-40%); }
  to   { transform: translateX(340%); }
`;

/**
 * [2026-08-12] THE TOP OF THIS PAGE WAS DEAD.
 *
 * The bays are anchored to the floor — cars park on the ground, so everything worth reading sat
 * in the bottom third and the upper two thirds were a black rectangle. Two things now live up
 * there, and both are honest atmosphere rather than data pretending to move:
 *
 *   FLOODLIGHTS  Singapore is a NIGHT race, the only one on the calendar lit end to end by
 *                floodlight. Slow beams drifting across the top is exactly what that looks like
 *                from the grandstand, and it gives the void a light source.
 *   CONFETTI     It is a podium. Streamers falling through the frame in the three metals is the
 *                one motion that belongs to this page and nowhere else on the board.
 *
 * Neither ever crosses a figure: the layer is clipped to the upper region and sits behind the
 * bays, which are transparent at the top and opaque where the text starts.
 */
const beamDrift = keyframes`
  0%, 100% { transform: translateX(-7%) rotate(-9deg); opacity: 0.10; }
  50%      { transform: translateX(7%) rotate(-4deg); opacity: 0.20; }
`;
const fall = keyframes`
  0%   { transform: translate3d(0, -151.2px, 0) rotate(0deg); opacity: 0; }
  9%   { opacity: 0.85; }
  92%  { opacity: 0.5; }
  100% { transform: translate3d(var(--dx), 799.2px, 0) rotate(760deg); opacity: 0; }
`;

const Sky = styled.div`
  position: absolute;
  left: 0; right: 0; top: 0;
  height: 72%;
  z-index: 2;
  overflow: hidden;
  pointer-events: none;

  .beam {
    position: absolute;
    top: -55%;
    width: 24%;
    height: 190%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.16), transparent 72%);
    filter: blur(20px);
    animation: ${beamDrift} 12s ease-in-out infinite;
    will-change: transform, opacity;
  }
  .beam.b1 { left: 6%; }
  .beam.b2 { left: 42%; animation-delay: -4s; }
  .beam.b3 { left: 74%; animation-delay: -8s; }

  /* Scoped to .bit, NOT to the bare element: the beams are <i> too, and a bare rule here would
     hand them the confetti's size, its falling animation and an opacity of zero. */
  i.bit {
    position: absolute;
    top: 0;
    width: 6.53px;
    height: 11.9px;
    border-radius: 1px;
    opacity: 0;
    animation: ${fall} var(--t) linear infinite;
    will-change: transform, opacity;
    animation-delay: var(--del);
  }

  @media (prefers-reduced-motion: reduce) {
    .beam, i.bit { animation: none; }
    i.bit { display: none; }
  }
`;

/* ═════════════════════════════════════════════════════════════ layout ═════ */

const View = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #0E0E15 0%, #14141C 58%, #191921 100%);

  /* The floor. A parc fermé is painted tarmac, so the page is given ground rather than a
     gradient that stops nowhere. */
  &::before {
    content: '';
    position: absolute; left: 0; right: 0; bottom: 0; height: 46%;
    background:
      radial-gradient(ellipse 60% 120% at 50% 100%, rgba(217,179,108,.055), transparent 72%),
      linear-gradient(180deg, transparent, rgba(0,0,0,.45)),
      ${LANE_SURFACE};
    opacity: 0.9;
  }
  /* Grain, so a large dark area does not band on a big panel. */
  &::after {
    content: '';
    position: absolute; inset: 0; opacity: 0.15; mix-blend-mode: overlay; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
  }
`;

const Header = styled.header`
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 26.88px;
  padding: 17.28px 34.56px 0;

  .mark {
    display: flex;
    align-items: center;
    gap: 13.44px;
    font-size: 38px;
    font-weight: 900;
    letter-spacing: 0.14em;
    white-space: nowrap;
  }
  .bars { display: flex; gap: 3px; transform: skewX(-16deg); }
  .bars i {
    display: block;
    width: 5.38px;
    height: 24.96px;
    background: ${RED};
  }
  .m1 { color: ${TEXT}; }
  .m2 { color: ${RED}; }

  .round { text-align: right; white-space: nowrap; }
  .r1 {
    font-size: 20px;
    font-weight: 700; letter-spacing: 0.24em;
    color: rgba(255, 255, 255, 0.75);
  }
  .r2 {
    font-size: 30px;
    font-weight: 900; letter-spacing: 0.08em; color: ${TEXT};
  }
`;

const Next = styled.div`
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  text-align: center;
  min-height: 1.2em;
  padding-top: 5.4px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: rgba(255, 255, 255, 0.75);
`;

const Bays = styled.div<{ $run: boolean }>`
  position: relative;
  z-index: 3;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1.18fr 1fr;
  align-items: end;
  gap: 26.88px;
  padding: 15.12px 46.08px 19.44px;

  /**
   * Three bays side by side stop working before the screen gets truly small: each one has to
   * hold a car three times its own height, a full name and a percentage, and by about 900px they
   * are competing for roughly 280px each. Stacked, every bay gets the full width and the cars
   * still drive in from the right — the podium order is carried by the metals and the numbers
   * painted on the floor rather than by which is in the middle.
   */
  /* [PM-SG FILL 2026-08-13] The 900px stacking rule is gone — same reason as the other views: it
     measured the viewport, not the canvas, and the canvas is clamped to 1600 wide so it could
     never fire on any screen this board actually runs on. The three bays always sit side by side. */

  ${(p) => p.$run && css`
    .bay   { animation: ${bayIn} 620ms ${EASE} backwards;
             animation-delay: calc(var(--d) * 1ms); }
    .paint { animation: ${paintIn} 900ms ${EASE} backwards;
             animation-delay: calc(var(--d) * 1ms + 200ms); }
    /* The car is the event: it lands after its own bay has settled. */
    .car   { animation: ${rollIn} 1600ms cubic-bezier(.16,.7,.24,1) backwards;
             animation-delay: calc(var(--d) * 1ms + 260ms); }
  `}
`;

const Bay = styled.div<{ $rank: number; $open: boolean }>`
  position: relative;
  min-width: 0;
  /* The winner's bay is TALLER and WIDER — the hierarchy the rostrum's steps used to carry,
     in a form that still reads from the back of a room. */
  height: ${(p) => (p.$rank === 0 ? '96%' : p.$rank === 1 ? '84%' : '78%')};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 10.8px 19.2px 16.2px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.028), transparent 42%),
    linear-gradient(180deg, transparent 55%, ${(p) => METALS[p.$rank].soft});
  border: 1px solid ${LINE};
  border-bottom: none;
  /* Chamfer on the outer top corner only, so the three bays lean towards the centre one. */
  clip-path: ${(p) => (p.$rank === 1
    ? 'polygon(0 0, calc(100% - 26.88px) 0, 100% 26.88px, 100% 100%, 0 100%)'
    : p.$rank === 2
      ? 'polygon(26.88px 0, 100% 0, 100% 100%, 0 100%, 0 26.88px)'
      : 'polygon(26.88px 0, calc(100% - 26.88px) 0, 100% 26.88px, 100% 100%, 0 100%, 0 26.88px)')};
  opacity: ${(p) => (p.$open ? 0.62 : 1)};

  /* THE POSITION, PAINTED ON THE FLOOR. Laid down in perspective rather than standing up as a
     numeral, because that is what a parc fermé bay actually looks like — and it stops the digit
     competing with the figure, which is the thing on this card worth reading. */
  /* ⚠ It sits HIGH in the bay, not at the floor. Laid flat at the bottom it was both invisible
     (a 66° rotation squashes a numeral to almost nothing) and hidden behind the car — which is
     what left the top two thirds of every bay as dead space. Higher up reads as further away
     under the same perspective, so it fills the void AND sits correctly behind the car. */
  .paint {
    position: absolute;
    left: 50%; top: 8%;
    transform: translateX(-50%) perspective(700px) rotateX(52deg);
    transform-origin: center top;
    font-size: ${(p) => (p.$rank === 0 ? '367px' : '292px')};
    font-weight: 900;
    line-height: 0.78;
    letter-spacing: -0.04em;
    color: ${(p) => METALS[p.$rank].m};
    opacity: 0.22;
    text-shadow: none;
    pointer-events: none;
    user-select: none;
  }

  /* The car sits ON the paint, nose right, in its own livery. */
  .car {
    position: relative;
    align-self: center;
    width: min(86%, ${(p) => (p.$rank === 0 ? '324px' : '280.8px')} * ${SIDE_CAR_RATIO});
    aspect-ratio: ${SIDE_CAR_RATIO};
    margin-bottom: 12.96px;
    color: var(--c);
    filter: drop-shadow(0 0 22px var(--g)) drop-shadow(0 10px 16px rgba(0, 0, 0, 0.7));
  }
  .car svg { width: 100%; height: 100%; display: block; position: relative; z-index: 2; }

  /* Heat off the engine cover. Sits BEHIND the car and above the floor, never over the text. */
  .heat {
    position: absolute;
    left: 50%; transform: translateX(-50%);
    bottom: 34%;
    width: 46%; height: 12%;
    background: radial-gradient(ellipse at 50% 100%, rgba(255, 255, 255, 0.2), transparent 70%);
    filter: blur(7px);
    animation: ${shimmer} 4.6s ease-in-out infinite;
    will-change: transform, opacity;
    pointer-events: none;
  }

  .nm {
    text-align: center;
    font-size: ${(p) => (p.$rank === 0 ? '40px' : '37px')};
    font-weight: 900;
    letter-spacing: 0.06em;
    color: ${TEXT};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .val {
    text-align: center;
    font-size: ${(p) => (p.$rank === 0 ? '65px' : '50px')};
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    color: ${(p) => METALS[p.$rank].m};
    text-shadow: none;
  }
  .meta {
    text-align: center;
    margin-top: 0.35em;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: rgba(255, 255, 255, 0.75);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .meta.gap { color: ${(p) => METALS[p.$rank].m}; opacity: 0.85; }

  /* An empty bay is a specific invitation, not a slogan. */
  .open-t {
    text-align: center;
    font-size: 40px;
    font-weight: 900; letter-spacing: 0.16em; color: rgba(255, 255, 255, 0.75);
  }
  .open-l {
    text-align: center; margin-top: 0.5em;
    font-size: 20px;
    font-weight: 700; letter-spacing: 0.14em; color: rgba(255, 255, 255, 0.75);
  }
`;

/** The kerb across the front of each bay, in that bay's metal. */
const Kerb = styled.div<{ $rank: number }>`
  position: relative;
  overflow: hidden;
  flex: 0 0 auto;
  height: 8.64px;
  margin: 8.64px calc(-1 * 19.2px) calc(-1 * 16.2px);
  background: repeating-linear-gradient(
    115deg,
    ${(p) => METALS[p.$rank].m} 0 14px,
    #F2F2F2 14px 28px);
  opacity: 0.82;

  &::after {
    content: '';
    position: absolute; top: 0; bottom: 0; width: 26%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
    animation: ${kerbRun} 6.5s linear infinite;
    will-change: transform;
    animation-delay: ${(p) => p.$rank * -2.1}s;
  }
`;

/* ═══════════════════════════════════════════════════════════════ view ═════ */

/**
 * The cars come back round every thirty seconds, the same beat RACE laps its field on and PIT
 * WALL re-runs its delivery on. Without it the arrival fired once and the rest of the visit was
 * a photograph — and somebody walking up thirty seconds in would never know the page moved.
 *
 * Comfortably longer than the arrival itself: the last car sets off at 1.5s and takes 1.6s to
 * park, so the whole sequence is done inside four seconds.
 */
const REPLAY_MS = 30000;

const PodiumView: React.FC<{ data: LeaderData; active: boolean }> = ({ data, active }) => {
  const [run, setRun] = useState(false);
  const [cycle, setCycle] = useState(0);

  /* The lap. Restarted whenever the page is opened, so the first replay is a full interval after
     arrival rather than the remainder of a clock that has been running in the background. */
  useEffect(() => {
    if (!active || prefersReducedMotion()) return;
    const t = window.setInterval(() => setCycle((n) => n + 1), REPLAY_MS);
    return () => window.clearInterval(t);
  }, [active]);

  /* Replays on every visit AND on every lap: the page is put on screen by a rotation, so the
     second visit has to look like the first. */
  useEffect(() => {
    if (!active || prefersReducedMotion()) { setRun(false); return; }
    setRun(false);
    const t = window.setTimeout(() => setRun(true), 20);
    return () => window.clearTimeout(t);
  }, [active, cycle]);

  /* [PM-SG-LB 2026-09-18] Already rank-ordered by the server, and every PM is ranked — there
     is no gate to filter on and no "nearest unclassified PM" to name any more. */
  const top = data.runners;

  /* Arrival order is REVERSED — third in first, the winner last. The delay is per RANK, so it
     does not matter that the bays are laid out 2-1-3 on screen. */
  const DELAY = [1500, 800, 100];

  const bay = (rank: number) => {
    const d: Runner | undefined = top[rank];
    return (
      <Bay
        key={rank}
        className="bay"
        $rank={rank}
        $open={!d}
        style={{
          '--d': DELAY[rank],
          '--c': d ? d.color : '#8A8A96',
          '--g': d ? d.glow : 'transparent',
        } as React.CSSProperties}
      >
        <span className="paint">{rank + 1}</span>

        {d ? (
          <>
            <i className="heat" />
            <div className="car">
              <Exhaust><i /><i /><i /><i /><i /><i /></Exhaust>
              <SideCarSvg idSuffix={`pod${rank}`} />
            </div>
            <div className="nm">{d.name.toUpperCase()}</div>
            <div className="val">{d.metric}</div>
            <div className="meta">{d.detail}</div>
            {/* Money, not efficiency points, on this page specifically: a podium is about the
                prize, and '$9,912 OFF THE LEAD' is a sentence a PM can act on where '+38.80'
                is a unit nobody carries around. RACE keeps points, beside its percent column. */}
            <div className="meta gap">
              {rank === 0 ? 'LEADER' : `$${fmtNum(d.gapMoney, 0)} OFF THE LEAD`}
            </div>
          </>
        ) : (
          <>
            <div className="open-t">NO RUNNER</div>
            <div className="open-l">THIS MONTH HAS FEWER THAN THREE PMs ON THE TABLE</div>
          </>
        )}

        <Kerb $rank={rank} />
      </Bay>
    );
  };

  /* Deterministic, not random: Math.random() here would re-scatter every confetti piece on every
     thirty-second poll, and the whole layer would visibly twitch each time the figures refresh. */
  const CONFETTI = Array.from({ length: 22 }, (_, i) => ({
    left: (i * 4.63 + 3) % 96,
    dx: `${((i % 5) - 2) * 2.4}vw`,
    t: `${7 + (i % 6) * 1.4}s`,
    del: `${-(i * 0.83).toFixed(2)}s`,
    c: [METALS[0].m, METALS[1].m, METALS[2].m, '#FFFFFF'][i % 4],
  }));

  return (
    <View>
      <Sky aria-hidden="true">
        <i className="beam b1" /><i className="beam b2" /><i className="beam b3" />
        {CONFETTI.map((c, i) => (
          <i
            key={i}
            className="bit"
            style={{
              left: `${c.left}%`,
              background: c.c,
              '--dx': c.dx,
              '--t': c.t,
              '--del': c.del,
            } as React.CSSProperties}
          />
        ))}
      </Sky>

      <Header>
        <div className="mark">
          <span className="bars"><i /><i /><i /></span>
          <span className="m1">PARC</span><span className="m2">FERMÉ</span>
        </div>
        <div className="round">
          {/* "Classification" was gate vocabulary. The figure is a running total now. */}
          <div className="r1">CUMULATIVE SINCE AUGUST</div>
          <div className="r2">{data.month}</div>
        </div>
      </Header>

      <Next>
        {top.length > 3 ? `NEXT UP · P4 ${top[3].name.toUpperCase()} · ${top[3].metric}` : ''}
      </Next>

      {/* Viewer's left is P2, centre P1, right P3 — the order the cars are parked in. */}
      <Bays $run={run}>{bay(1)}{bay(0)}{bay(2)}</Bays>
    </View>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(PodiumView);
